import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "@phosphor-icons/react";
import { useLang } from "@/lib/LanguageContext";
import PageShell from "@/components/PageShell";
import ImageHero from "@/components/ImageHero";
import Seo, { faqSchema } from "@/components/Seo";

export default function MembershipPage() {
  const { t } = useLang();
  const m = t.membership;

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        name: "Book & Smile Membership",
        provider: { "@id": "https://bookandsmiles.com/#business" },
        areaServed: "Paris, France",
        offers: m.tiers.map((tier) => ({
          "@type": "Offer",
          name: tier.name,
          description: tier.desc,
        })),
      },
      faqSchema(m.faqs),
    ],
    [m]
  );

  return (
    <PageShell>
      <Seo title={m.seoTitle} description={m.seoDesc} schema={schema} />

      <ImageHero testId="membership-hero" image="cafeLaptop" eyebrow={m.eyebrow} titleA={m.title} titleB={m.titleB} text={m.intro} />

      <section data-testid="membership-tiers" className="py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-px bg-stone-200 border border-stone-200">
          {m.tiers.map((tier) => (
            <div
              key={tier.id}
              data-testid={`membership-tier-${tier.id}`}
              className={`p-10 sm:p-12 flex flex-col ${tier.highlight ? "bg-slate-900 text-stone-200" : "bg-stone-50"}`}
            >
              <p className={`font-serif text-4xl ${tier.highlight ? "text-stone-50" : "text-stone-900"}`}>{tier.name}</p>
              <p className={`mt-5 leading-relaxed ${tier.highlight ? "text-stone-400" : "text-stone-600"}`}>{tier.desc}</p>
              <ul className="mt-8 space-y-4 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={16} className={`mt-1 shrink-0 ${tier.highlight ? "text-stone-300" : "text-stone-900"}`} />
                    <span className={`text-sm leading-relaxed ${tier.highlight ? "text-stone-300" : "text-stone-700"}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                data-testid={`membership-cta-${tier.id}`}
                to="/contact"
                className={`mt-10 inline-flex items-center justify-center gap-3 px-6 py-4 text-sm font-medium transition-colors ${
                  tier.highlight
                    ? "bg-stone-50 text-slate-900 hover:bg-stone-200"
                    : "bg-slate-900 text-stone-50 hover:bg-slate-800"
                }`}
              >
                {tier.cta}
                <ArrowRight size={16} />
              </Link>
            </div>
          ))}
        </div>
        <p className="max-w-7xl mx-auto mt-8 text-sm text-stone-500 italic">{m.note}</p>
      </section>

      <section data-testid="membership-faq" className="bg-stone-100 border-t border-stone-200 py-16 sm:py-32 px-6 lg:px-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-base md:text-lg text-stone-900">{m.faqTitle}</h2>
          <div className="mt-10 space-y-10">
            {m.faqs.map((f) => (
              <div key={f.q} className="border-b border-stone-200 pb-8">
                <h3 className="font-medium text-stone-900 text-lg">{f.q}</h3>
                <p className="mt-3 text-stone-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PageShell>
  );
}
